import { EnvironmentService } from '../../../environment/environment-service';
import {
  DashboardConfig,
  DashboardId,
} from '../../common/components/dashboards/types';
import { Domains } from '../../constants';
import {
  defaultDashboardConfigs,
  iPexDashboardConfigs,
  jtbDashboardConfigs,
  jtDashboardConfigs,
  mixiDashboardConfigs,
  necnsiDashboardConfigs,
  nikkiDashboardConfigs,
  nssolDashboardConfigs,
  patagoniaDashboardConfigs,
  shipHealthcareDashboardConfigs,
  swanDashboardConfigs,
  tdHoldingsDashboardConfigs,
  washiDashboardConfigs,
} from '../../settings/dashboard-configs/dashboard-configs-json';
import { Hasher } from '../hasher';
import {
  DomainPreferences,
  DomainSettings,
  HasDependencies,
  hashString,
} from '../types';
import { DashboardNotFoundError } from './dashboard-errors';
import { DashboardService } from './dashboard-service';
import { FullViewDashboardService } from './full-view-dashboard-service';

export class DomainViewDashboardService
  implements DashboardService, HasDependencies
{
  constructor(
    private readonly fullViewDashboardService: FullViewDashboardService,
    private readonly latestPreferences: DomainPreferences,
    private readonly latestSettings: DomainSettings,
    private readonly domain: Domains,
    private readonly environmentService: EnvironmentService
  ) {}

  public getDeps = () => [
    hashString([
      ...this.fullViewDashboardService.getDeps(),
      Hasher.hash(this.latestPreferences),
      Hasher.hash(this.latestSettings),
      this.domain,
    ]),
  ];

  public listDashboards = async () => {
    const companyAllowedDashboards: string[] =
      this.latestSettings.allowedDashboards?.map((d) => d.id) ?? [];
    const allDashboards = await this.fullViewDashboardService.listDashboards();
    return allDashboards.filter((d) => companyAllowedDashboards.includes(d.id));
  };

  public listCustomDashboardConfigs = async () => {
    let dashboardConfigs: DashboardConfig[];
    switch (this.domain) {
      case Domains.SWAN:
        dashboardConfigs = swanDashboardConfigs;
        break;
      case Domains.JT:
        dashboardConfigs = jtDashboardConfigs;
        break;
      case Domains.JTB:
        dashboardConfigs = jtbDashboardConfigs;
        break;
      case Domains.SHIP_HEALTHCARE:
        dashboardConfigs = shipHealthcareDashboardConfigs;
        break;
      case Domains.NIKKI:
        dashboardConfigs = nikkiDashboardConfigs;
        break;
      case Domains.MIXI_COMPANY_WIDE:
        dashboardConfigs = mixiDashboardConfigs;
        break;
      case Domains.WASHI:
        dashboardConfigs = washiDashboardConfigs;
        break;
      case Domains.PATAGONIA:
        dashboardConfigs = patagoniaDashboardConfigs;
        break;
      case Domains.NECNSI:
        dashboardConfigs = necnsiDashboardConfigs;
        break;
      case Domains.NSSOL:
        dashboardConfigs = nssolDashboardConfigs;
        break;
      case Domains.I_PEX:
        dashboardConfigs = iPexDashboardConfigs;
        break;
      case Domains.TD_HOLDINGS:
        dashboardConfigs = tdHoldingsDashboardConfigs;
        break;
      default:
        dashboardConfigs = defaultDashboardConfigs;
        break;
    }
    if (!this.environmentService.isProd) {
      return dashboardConfigs;
    }
    return dashboardConfigs.filter((config) => !config.qaOnly);
  };

  public getCustomDashboardConfig = async (dashboardId: DashboardId) => {
    const dashboardConfigs = await this.listCustomDashboardConfigs();
    const dashboardConfig =
      dashboardConfigs.find((dc) => dc.id === dashboardId) ?? null;
    if (dashboardConfig === null) {
      return new DashboardNotFoundError(dashboardId);
    } else {
      return dashboardConfig;
    }
  };
}
